import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  checkLoginUid,
  getCurrentUid,
  setDisableAutoLogin,
} from "@/api/common";
import {
  ConfigPageAuthMode,
  isShareAssistLink,
} from "@/hooks/useConfigPage/access";
import { goToPageV2, navHide } from "@/utils/common";
import { isHostLoggedOut } from "@/utils/host-login-state";
import { redirectToHostLogin } from "@/utils/host-login-redirect";
import "./index.less";
import Page from "./page";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import useFetchConfigPage from "@/hooks/useConfigPage/useFetchConfigPage";
import {
  ConfigPageProvider,
  useConfigPage,
} from "@/hooks/useConfigPage/useConfigPage";
import { getQueryHashString } from "@/utils/url";
import ScrollContainer from "./ScrollContainer";
import AppNavBarWithConfig from "@/hooks/useConfigPage/AppNavBarWithConfig";

const ConfigPageContent = () => {
  const { configPageQuery, ensureLogin, isGuest, loginRequired } =
    useConfigPage();
  const isConfigReady =
    configPageQuery?.status === "success" && Boolean(configPageQuery.data);
  const shouldForceLogin = isConfigReady && isGuest && loginRequired === 1;

  useEffect(() => {
    if (shouldForceLogin) {
      ensureLogin?.();
    }
  }, [ensureLogin, shouldForceLogin]);

  if (!isConfigReady || shouldForceLogin) {
    return null;
  }

  return (
    <>
      <AppNavBarWithConfig />
      <ScrollContainer>
        {isGuest && loginRequired === 0 && (
          <div className="config-page-login-guide">
            <span>登录查看更多活动优惠</span>
            <button type="button" onClick={() => ensureLogin?.()}>
              立即登录
            </button>
          </div>
        )}
        <Page />
      </ScrollContainer>
    </>
  );
};

const ConfigPage = () => {
  useLayoutEffect(() => {
    // 隐藏导航栏
    navHide();
  }, []);

  useEffect(() => {
    // 监听 hash 变化
    const handleHashChange = () => {
      window?.location?.reload();
    };

    const handleNavHide = () => {
      navHide();
      setTimeout(() => {
        navHide();
      }, 100);
    }; // 100 毫秒内最多执行一次

    window.addEventListener("hashchange", handleHashChange);
    document.addEventListener("visibilitychange", handleNavHide);

    // 清理事件监听器
    return () => {
      document.removeEventListener("visibilitychange", handleNavHide);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // 分享逻辑已封装到ConfigPageProvider中

  const pageCode = getQueryHashString("pageCode") || "";
  const versionCode = getQueryHashString("versionCode") || "";
  const previewType = getQueryHashString("previewType") || "";
  const shareUid = getQueryHashString("shareUid") || "";
  const shareType = getQueryHashString("shareType") || "";
  // 分享助力链接必须登录，不进入游客态（见 access.ts 的 isShareAssistLink）
  const shareAssist = isShareAssistLink({ shareUid, shareType });
  const [authMode, setAuthMode] = useState<ConfigPageAuthMode | undefined>();
  const checkingLoginRef = useRef(false);
  const handleAuthenticated = useCallback(() => {
    setAuthMode("authenticated");
  }, []);

  useEffect(() => {
    // APP/小程序 webview 内 sessionUid 由联登 SDK 在首个请求时才写入，
    // 首屏用同步缓存判断会把已登录用户误判为未登录。
    // 这里先做一次不跳转登录的探测，探测完成前不发起配置请求，
    // 避免已登录用户先拿到 guest 裁剪版页面
    let cancelled = false;
    setDisableAutoLogin(true);
    checkLoginUid().then(async (uid) => {
      if (cancelled) {
        return;
      }
      if (uid) {
        setAuthMode("authenticated");
        return;
      }
      if (!shareAssist) {
        setAuthMode("guest");
        return;
      }

      // 分享助力链接：在拉配置之前就把用户送去登录，不经过游客态。
      // 放在这里而不是等 loginRequired 回来再判，是为了避开游客态的两个副作用：
      // 一次注定被裁剪的 guest 配置请求，以及 openedAsGuest 快照对助力上报的抑制
      if (isHostLoggedOut()) {
        // 宿主（小程序）已声明退登：webview 内的联登会话不可信，必须交还宿主登录页；
        // forceLogin=1 表示取消登录就退出该 webview，不降级成游客态
        redirectToHostLogin(true);
        return;
      }
      // 恢复联登 SDK 的"未登录自动跳登录"兜底，由它按环境跳转（APP JSBridge /
      // 小程序登录页 / H5 授权地址）；正常情况下这里会直接离开当前页面
      setDisableAutoLogin(false);
      const loggedInUid = await getCurrentUid();
      if (cancelled) {
        return;
      }
      if (loggedInUid) {
        setAuthMode("authenticated");
        return;
      }
      // 兜底：登录被取消且没能离开页面时，降级成普通游客态，
      // 由 loginRequired 决定是再拉一次登录还是展示游客版页面，避免白屏
      setDisableAutoLogin(true);
      setAuthMode("guest");
    });

    return () => {
      cancelled = true;
    };
  }, [shareAssist]);

  useEffect(() => {
    // 免登录访问专题页后跳转二级页登录，返回时感知登录态变化并刷新。
    // 仅游客态需要监听：已登录用户切前台无需重复探测。
    //
    // 【只服务 APP / 外部浏览器，不处理小程序】小程序里 checkLoginUid 会被
    // isHostLoggedOut 短路，reload 也无效（URL 没变，重载后又读到
    // hostLoginState=0），H5 侧唯一能做的恢复动作是请宿主重开容器页。但
    // wx.miniProgram.redirectTo / my.redirectTo 作用于小程序页面栈的【栈顶】，
    // 而不是发起调用的这个 webview 所在的页面，H5 无从判断自己是不是栈顶——
    // 这个信息缺口在 H5 侧补不上。曾尝试用 visibilitychange 的
    // hidden → visible 转换来近似「本页回到前台」，实测结果：
    //   - 微信 iOS / 安卓：webview 被覆盖时 visibilityState 不变，永不触发
    //   - 支付宝安卓：不触发，或 visible 晚于 componentDidShow
    //   - 支付宝 iOS：触发，但与宿主的 loginSuccess 重建撞在同一时刻，
    //     两个 redirectTo 交错导致页面栈多出一层（左滑返回还在专题页）
    // 即在主要端上从未生效、只在一个端生效并且生效即出错，故整体移除。
    //
    // 小程序的恢复由宿主完成：登录页 setData(loginSuccess) 通知容器页，
    // 容器页 redirectTo 自身重建并用新 otp 重拼 src（见 host-login-redirect 的
    // replace 模式）。那条链路的时序由小程序框架保证，四端行为一致
    if (authMode !== "guest" || isHostLoggedOut()) {
      return;
    }

    const handlePageResume = async () => {
      if (document.visibilityState !== "visible" || checkingLoginRef.current) {
        return;
      }
      checkingLoginRef.current = true;
      try {
        // APP 走 JSBridge getLoginState（原生是权威源，实时且无残留会话问题），
        // 浏览器走接口探测。拿到 uid 时 checkLoginUid 已写好 sessionUid，
        // reload 后 getCachedUid() 即可读到。
        // reload 只影响本 webview，不碰任何页面栈，因此三个信号都可以驱动它
        const uid = await checkLoginUid({ force: true });
        if (uid) {
          window.location.reload();
        }
      } finally {
        checkingLoginRef.current = false;
      }
    };

    document.addEventListener("visibilitychange", handlePageResume);
    window.addEventListener("pageshow", handlePageResume);
    window.addEventListener("focus", handlePageResume);

    return () => {
      document.removeEventListener("visibilitychange", handlePageResume);
      window.removeEventListener("pageshow", handlePageResume);
      window.removeEventListener("focus", handlePageResume);
    };
  }, [authMode]);

  const query = useFetchConfigPage({
    authMode,
    pageCode,
    versionCode,
    previewType,
    shareUid,
    // 登录态探测完成前不发请求，否则会用错误的 authMode 拉一次配置
    enabled: Boolean(authMode),
  });

  useEffect(() => {
    // 监听页面数据变化
    if (query?.status === "success" && authMode === "authenticated") {
      if (!query?.data?.moduleList || query?.data?.moduleList.length === 0) {
        goToPageV2({
          hash: "/expiredPage",
          params: {
            expiredTitle: query?.data?.emptyTips || "",
          },
          isReplace: true,
        });
      }
    }
  }, [query]);

  useEffect(() => {
    // 监听页面数据变化
    if (query?.data?.pageTitle) {
      document.title = query?.data?.pageTitle;
    }
  }, [query?.data?.pageTitle]);

  // 登录态探测完成前不挂载 Provider：ConfigPageProvider 会在挂载瞬间快照 isGuest
  // 作为 openedAsGuest，authMode 未确定时挂载会把真游客记成非游客
  if (!authMode) {
    return <div className="config_pageCntainer" />;
  }

  return (
    <div className="config_pageCntainer">
      <ConfigPageProvider
        configPageQuery={query}
        authMode={authMode}
        onAuthenticated={handleAuthenticated}
      >
        <ConfigPageContent />
      </ConfigPageProvider>
    </div>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigPage />
    </QueryClientProvider>
  );
};
export default App;
